import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Menu, Sun, Moon, Search, MapPin, AlertTriangle, Database, User, LogIn, Link2, Heart, BookOpen, FileText, TreePine, Briefcase, LayoutDashboard, Settings, LogOut, CreditCard, ChevronDown, Zap, Camera, UserPlus, FileCheck, Lock, FolderSearch, Scale, Gauge, HelpCircle, ArrowLeft, Mail, Eye, Bot } from 'lucide-react';
import { motion } from 'framer-motion';
import { Logo } from '@/components/Logo';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import { hasProAccess } from '@/components/MatchesPaywall';
import TrialCountdownPill from '@/components/TrialCountdownPill';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [pendingReviewCount, setPendingReviewCount] = useState(0);
  const [scraperErrorCount, setScraperErrorCount] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated, loading, logout } = useAuth();
  const { isDarkMode: darkMode, toggleDarkMode } = useTheme();

  // Track scroll position for shrinking header.
  // Hysteresis band (shrink > 48px, expand < 8px) prevents the header
  // from oscillating when layout reflow from the h-20↔h-16 transition
  // tugs window.scrollY back across a single threshold. rAF-throttled
  // so the 300ms height transition can't fire extra scroll events that
  // re-flip state mid-animation.
  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        setScrolled((prev) => {
          if (!prev && y > 48) return true;
          if (prev && y < 8) return false;
          return prev;
        });
        ticking = false;
      });
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Prime initial state without the threshold gap.
    setScrolled(window.scrollY > 48);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fetch pending review count for admins
  useEffect(() => {
    const fetchPendingCount = async () => {
      if (!isAuthenticated || !user?.is_admin) return;
      try {
        const response = await fetch(`${API}/cases/review/stats`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          setPendingReviewCount(data.pending || 0);
        }
      } catch (error) {
        console.error('Failed to fetch pending count:', error);
      }
    };
    fetchPendingCount();
    // Refresh every 60 seconds
    const interval = setInterval(fetchPendingCount, 60000);
    return () => clearInterval(interval);
  }, [isAuthenticated, user?.is_admin]);

  // Fetch scraper error count for admins (polls every 5 min)
  useEffect(() => {
    const fetchScraperErrors = async () => {
      if (!isAuthenticated || !user?.is_admin) return;
      try {
        const response = await fetch(`${API}/admin/scrape/status`, { credentials: 'include' });
        if (response.ok) {
          const data = await response.json();
          const total = (data.sources || []).reduce(
            (sum, s) => sum + (s.last_scrape_errors || 0), 0);
          setScraperErrorCount(total);
        }
      } catch (error) {
        console.error('Failed to fetch scraper status:', error);
      }
    };
    fetchScraperErrors();
    const interval = setInterval(fetchScraperErrors, 300000); // 5 min
    return () => clearInterval(interval);
  }, [isAuthenticated, user?.is_admin]);

  // Helper to display consistent plan names
  const getPlanDisplayName = (plan, trialDaysRemaining) => {
    switch (plan) {
      case 'trial': 
        return trialDaysRemaining !== undefined 
          ? `Trial (${trialDaysRemaining}d left)` 
          : 'Trial';
      case 'pro': return 'Pro';
      case 'organization': return 'Organization';
      case 'professional': return 'Professional';
      case 'community': return 'Free';
      case 'free': return 'Free';
      default: return 'Free';
    }
  };
  
  // Calculate trial days remaining
  const getTrialDaysRemaining = () => {
    if (user?.subscription_plan === 'trial' && user?.trial_ends_at) {
      const endsAt = new Date(user.trial_ends_at);
      const now = new Date();
      const diffTime = endsAt - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(0, diffDays);
    }
    return undefined;
  };
  
  const trialDaysRemaining = getTrialDaysRemaining();

  // Grouped navigation links for mobile menu
  // Order follows: Explore → Analyze → Act
  const isPro = hasProAccess(user);
  const isOrg = user?.subscription_plan === 'organization';
  
  // Record Reconciliation only for Professional/Organization users
  const investigateLinks = [
    { href: '/browse', label: 'Explore Cases', icon: Search, primary: true, description: 'Search 62,000+ cases' },
    { href: '/map', label: 'Map View', icon: MapPin, secondary: true, description: 'Explore cases by location' },
    { href: '/potential-matches', label: 'Case Matches', icon: Link2, featured: true, badge: isPro ? 'AI' : null, locked: !isPro, description: 'AI-assisted similarity matching' },
    // Record Reconciliation - Organization/Professional only, NOT for Community
    ...(isPro || isOrg ? [{ 
      href: '/record-reconciliation', 
      label: 'Record Reconciliation', 
      icon: FolderSearch, 
      badge: isOrg ? 'ORG' : 'PRO',
      description: 'Registry-based identity linking'
    }] : []),
    // Cluster Detection removed from top nav - will be integrated into Map/Matches as analytical overlay
    // Forensic Blind Spots - Professional/Organization only
    ...(isPro || isOrg ? [{
      href: '/forensic-blind-spots',
      label: 'Blind Spots',
      icon: Eye,
      badge: isPro ? 'PRO' : 'ORG',
      description: 'Forensic data gap registry'
    }] : []),
  ];
  
  const contributeLinks = [
    { href: '/report', label: 'Submit Case', icon: AlertTriangle, urgent: true, description: 'Submit a missing person case' },
    { href: '/donate', label: 'Donate', icon: Heart, highlight: true },
  ];
  
  const learnLinks = [
    { href: '/genealogy', label: 'Genealogy', icon: TreePine, comingSoon: true },
    { href: '/resources', label: 'Resources', icon: BookOpen },
    { href: '/blog', label: 'Blog', icon: FileText },
  ];

  // Combined for desktop nav (flat list)
  const navLinks = [...investigateLinks, ...learnLinks.slice(0, 1), ...learnLinks.slice(1), contributeLinks[0], contributeLinks[1]];

  const isActive = (path) => location.pathname === path;

  return (
    <motion.header
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      className={`sticky top-0 z-50 border-b transition-all duration-300 ${
        scrolled 
          ? 'bg-white/80 dark:bg-slate-950/80 backdrop-blur-md border-slate-200/50 dark:border-slate-800/50 shadow-sm' 
          : 'glass border-slate-200 dark:border-slate-800'
      }`}
      role="banner"
    >
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8" aria-label="Main navigation">
        {/* Mobile: Compact header when scrolled */}
        <div className={`flex items-center justify-between transition-all duration-200 ${
          scrolled ? 'h-16 md:h-20 lg:h-22' : 'h-20 md:h-24 lg:h-28'
        }`}>
          {/* Logo - Horizontal for header */}
          <div className={`transition-all duration-200 ${scrolled ? 'scale-[0.8] origin-left' : ''}`}>
            <Logo size={scrolled ? 'default' : 'large'} variant="horizontal" />
          </div>

          {/* Desktop Navigation - Streamlined */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Why LinkTheMissing - Trust builder before exploring */}
            <Link to="/why">
              <Button
                variant={isActive('/why') ? 'default' : 'ghost'}
                size="sm"
                className={`gap-1.5 rounded-full px-3 ${
                  isActive('/why')
                    ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                    : 'text-trust-blue-600 dark:text-trust-blue-400'
                }`}
              >
                <HelpCircle className="w-4 h-4" />
                Why Us
              </Button>
            </Link>

            {/* Primary nav items */}
            <Link to="/browse">
              <Button
                variant={isActive('/browse') ? 'default' : 'ghost'}
                size="sm"
                className={`gap-1.5 rounded-full px-3 ${
                  isActive('/browse')
                    ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                    : ''
                }`}
              >
                <Search className="w-4 h-4" />
                Explore
              </Button>
            </Link>
            
            <Link to="/map">
              <Button
                variant={isActive('/map') ? 'default' : 'ghost'}
                size="sm"
                className={`gap-1.5 rounded-full px-3 ${
                  isActive('/map')
                    ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                    : ''
                }`}
              >
                <MapPin className="w-4 h-4" />
                Map
              </Button>
            </Link>
            
            <Link to="/potential-matches">
              <Button
                variant={isActive('/potential-matches') ? 'default' : 'ghost'}
                size="sm"
                className={`gap-1.5 rounded-full px-3 ${
                  isActive('/potential-matches')
                    ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                    : 'bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/30 dark:to-indigo-950/30 border border-purple-200 dark:border-purple-800 text-purple-700 dark:text-purple-300'
                }`}
              >
                <Link2 className="w-4 h-4 text-purple-500" />
                Matches
                {hasProAccess(user) ? (
                  <span className="px-1 py-0.5 text-[9px] font-bold bg-purple-500 text-white rounded-full">AI</span>
                ) : (
                  <Lock className="w-3 h-3 text-gray-400" />
                )}
              </Button>
            </Link>

            {/* More dropdown for secondary items */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-1.5 rounded-full px-3 relative">
                  More
                  <ChevronDown className="w-3 h-3" />
                  {isAuthenticated && user?.is_admin && scraperErrorCount > 0 && (
                    <Badge
                      variant="destructive"
                      className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 text-[10px] leading-none"
                      data-testid="scraper-error-badge"
                    >
                      {scraperErrorCount > 99 ? '99+' : scraperErrorCount}
                    </Badge>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/why" className="flex items-center gap-2 cursor-pointer text-trust-blue-600">
                    <HelpCircle className="w-4 h-4" />
                    Why LinkTheMissing
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled className="flex items-center gap-2 cursor-not-allowed opacity-60">
                  <TreePine className="w-4 h-4" />
                  Genealogy
                  <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 bg-amber-100 text-amber-700 border-amber-300">Soon</Badge>
                </DropdownMenuItem>
                {/* Record Reconciliation - Only for Professional/Organization */}
                {(isPro || isOrg) && (
                  <DropdownMenuItem asChild>
                    <Link to="/record-reconciliation" className="flex items-center gap-2 cursor-pointer text-purple-600">
                      <FolderSearch className="w-4 h-4" />
                      Record Reconciliation
                      <Badge variant="outline" className="ml-auto text-[10px] px-1 py-0 bg-purple-100 text-purple-700 border-purple-200">{isOrg ? 'ORG' : 'PRO'}</Badge>
                    </Link>
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem asChild>
                  <Link to="/resources" className="flex items-center gap-2 cursor-pointer">
                    <BookOpen className="w-4 h-4" />
                    Resources
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  {/* Direct anchor to the static blog index — bypasses
                      React Router so /blog/ doesn't briefly render the
                      old SPA BlogPage before redirecting. */}
                  <a href="/blog/index.html" className="flex items-center gap-2 cursor-pointer">
                    <FileText className="w-4 h-4" />
                    Blog
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/facial-search" className="flex items-center gap-2 cursor-pointer text-indigo-600">
                    <Camera className="w-4 h-4" />
                    Facial Search
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/contact" className="flex items-center gap-2 cursor-pointer">
                    <HelpCircle className="w-4 h-4" />
                    Contact Us
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/report" className="flex items-center gap-2 cursor-pointer text-red-600">
                    <AlertTriangle className="w-4 h-4" />
                    Submit Case
                  </Link>
                </DropdownMenuItem>
                {isAuthenticated && user?.is_admin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/admin/dashboard" className="flex items-center justify-between cursor-pointer text-emerald-600">
                        <span className="flex items-center gap-2">
                          <Gauge className="w-4 h-4" />
                          Admin Dashboard
                        </span>
                        {scraperErrorCount > 0 && (
                          <Badge
                            variant="destructive"
                            className="ml-2 h-5 px-1.5 text-xs"
                            data-testid="scraper-error-badge-dropdown"
                          >
                            {scraperErrorCount} scraper err
                          </Badge>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/investigate" className="flex items-center gap-2 cursor-pointer text-violet-600">
                        <Bot className="w-4 h-4" />
                        AI Investigator
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/add-case" className="flex items-center gap-2 cursor-pointer text-trust-blue-600">
                        <UserPlus className="w-4 h-4" />
                        Add Missing Person
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/case-review" className="flex items-center justify-between cursor-pointer text-purple-600">
                        <span className="flex items-center gap-2">
                          <FileCheck className="w-4 h-4" />
                          Case Review
                        </span>
                        {pendingReviewCount > 0 && (
                          <Badge variant="destructive" className="ml-2 h-5 px-1.5 text-xs">
                            {pendingReviewCount}
                          </Badge>
                        )}
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/duplicates" className="flex items-center gap-2 cursor-pointer text-rose-600">
                        <Scale className="w-4 h-4" />
                        Duplicate Review
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/merge-cases" className="flex items-center gap-2 cursor-pointer text-indigo-600">
                        <FolderSearch className="w-4 h-4" />
                        Merge Cases
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/facial-matches" className="flex items-center gap-2 cursor-pointer text-cyan-600">
                        <Camera className="w-4 h-4" />
                        Facial Matches
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/jobs" className="flex items-center gap-2 cursor-pointer text-amber-600">
                        <Zap className="w-4 h-4" />
                        Background Jobs
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/admin/external" className="flex items-center gap-2 cursor-pointer text-green-600">
                        <Database className="w-4 h-4" />
                        External Cases
                      </Link>
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <Link to="/donate">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 rounded-full px-3 text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-950/30"
              >
                <Heart className="w-4 h-4 text-pink-500" />
                Donate
              </Button>
            </Link>
            
            {/* Workspace - Only for org users */}
            {isAuthenticated && user?.organization_id && (
              <Link to="/org/dashboard">
                <Button
                  variant={isActive('/org/dashboard') ? 'default' : 'ghost'}
                  size="sm"
                  className={`gap-1.5 rounded-full px-3 ${
                    isActive('/org/dashboard')
                      ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                      : 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
                  }`}
                  data-testid="nav-workspace"
                >
                  <Briefcase className="w-4 h-4 text-blue-500" />
                  Workspace
                </Button>
              </Link>
            )}
          </div>

          {/* Right side actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Active trial countdown pill (renders only for trial users) */}
            <TrialCountdownPill />

            {/* Dark mode toggle - Hidden on mobile, visible on desktop */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleDarkMode}
              className="hidden md:flex rounded-full"
              data-testid="theme-toggle"
            >
              {darkMode ? (
                <Sun className="w-5 h-5 text-yellow-500" />
              ) : (
                <Moon className="w-5 h-5 text-slate-600" />
              )}
            </Button>

            {/* Auth Buttons - Desktop */}
            <div className="hidden lg:flex items-center gap-2">
              {loading ? (
                <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 animate-pulse" />
              ) : isAuthenticated && user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="gap-2 rounded-full pr-2">
                      <Avatar className="w-8 h-8">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-safety-orange-100 text-safety-orange-700 text-sm">
                          {user.name?.charAt(0)?.toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden xl:inline">{user.name?.split(' ')[0]}</span>
                      <ChevronDown className="w-4 h-4 opacity-50" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium">{user.name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/dashboard" className="cursor-pointer">
                        <LayoutDashboard className="w-4 h-4 mr-2" />
                        Dashboard
                      </Link>
                    </DropdownMenuItem>
                    {user?.organization_id && (
                      <DropdownMenuItem asChild>
                        <Link to="/org/dashboard" className="cursor-pointer flex flex-col items-start">
                          <div className="flex items-center">
                            <Briefcase className="w-4 h-4 mr-2 text-blue-500" />
                            <span className="text-blue-700 dark:text-blue-400">Workspace</span>
                          </div>
                          <span className="text-[10px] text-muted-foreground ml-6">Org queue, matches & cases</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    {['professional', 'organization'].includes(user.subscription_plan) && !user?.organization_id && (
                      <DropdownMenuItem asChild>
                        <Link to="/workspace" className="cursor-pointer flex flex-col items-start">
                          <div className="flex items-center">
                            <Briefcase className="w-4 h-4 mr-2" />
                            My Workspace
                          </div>
                          <span className="text-[10px] text-muted-foreground ml-6">Track cases, matches & notes</span>
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="cursor-pointer">
                        <Settings className="w-4 h-4 mr-2" />
                        Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild>
                      <Link to="/pricing" className="cursor-pointer">
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscription
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem 
                      className="cursor-pointer text-red-600 focus:text-red-600"
                      onClick={async () => {
                        await logout();
                        navigate('/');
                      }}
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      Log out
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <>
                  <Link to="/login">
                    <Button variant="ghost" className="rounded-full" data-testid="nav-login">
                      Sign In
                    </Button>
                  </Link>
                  <Link to="/register">
                    <Button className="rounded-full bg-trust-blue-600 hover:bg-trust-blue-700 text-white shadow-md shadow-trust-blue-600/30" data-testid="nav-register">
                      Sign Up
                    </Button>
                  </Link>
                </>
              )}
            </div>

            {/* Mobile menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="rounded-full h-9 w-9" data-testid="mobile-menu-trigger">
                  <Menu className="w-5 h-5" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80 overflow-y-auto">
                <div className="flex flex-col gap-4 mt-8 pb-8">
                  {/* Mobile Auth Section */}
                  {loading ? (
                    <div className="h-12 bg-slate-200 dark:bg-slate-700 animate-pulse rounded-xl" />
                  ) : isAuthenticated && user ? (
                    <div className="space-y-2">
                      <Link to="/dashboard" onClick={() => setIsOpen(false)}>
                        <div className="flex items-center gap-3 p-3 rounded-xl bg-slate-100 dark:bg-slate-800">
                          <Avatar className="w-10 h-10">
                            <AvatarImage src={user.picture} alt={user.name} />
                            <AvatarFallback className="bg-safety-orange-100 text-safety-orange-700">
                              {user.name?.charAt(0)?.toUpperCase() || 'U'}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <p className="font-medium">{user.name}</p>
                            <p className="text-xs text-muted-foreground">{getPlanDisplayName(user.subscription_plan, trialDaysRemaining)} Plan</p>
                          </div>
                        </div>
                      </Link>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <Link to="/login" onClick={() => setIsOpen(false)} className="flex-1">
                        <Button variant="outline" className="w-full">Sign In</Button>
                      </Link>
                      <Link to="/register" onClick={() => setIsOpen(false)} className="flex-1">
                        <Button className="w-full bg-trust-blue-600 hover:bg-trust-blue-700 text-white shadow-md shadow-trust-blue-600/30">Sign Up</Button>
                      </Link>
                    </div>
                  )}

                  <div className="border-t pt-4" />

                  {/* Premium Workspace - For Pro/Org users */}
                  {isAuthenticated && user && (user.subscription_plan === 'professional' || user.subscription_plan === 'organization') && (
                    <Link to="/workspace" onClick={() => setIsOpen(false)}>
                      <div className={`w-full flex flex-col items-start gap-1 rounded-xl p-4 ${
                        isActive('/workspace')
                          ? 'bg-trust-blue-500 text-white'
                          : 'bg-trust-blue-500 text-white'
                      }`}>
                        <div className="flex items-center gap-3">
                          <Briefcase className="w-5 h-5" />
                          <span className="font-medium">My Workspace</span>
                        </div>
                        <p className="text-[11px] text-trust-blue-100 ml-8">Your private area to track cases, matches & notes</p>
                      </div>
                    </Link>
                  )}

                  {/* === INVESTIGATE GROUP === */}
                  <div className="space-y-2">
                    {investigateLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                      >
                        {link.primary && !isActive(link.href) ? (
                          /* Primary action - Explore Cases - visually dominant */
                          <div className="relative w-full rounded-xl py-4 px-4 bg-gradient-to-r from-slate-900 to-slate-800 dark:from-slate-800 dark:to-slate-700 border-l-4 border-safety-orange-500 shadow-md">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-safety-orange-500/20 flex items-center justify-center">
                                <link.icon className="w-5 h-5 text-safety-orange-500" />
                              </div>
                              <div>
                                <span className="font-semibold text-white">{link.label}</span>
                                {link.description && (
                                  <p className="text-[11px] text-slate-400">{link.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : link.featured && !isActive(link.href) ? (
                          /* Featured action - Case Matches with AI - slightly softer purple */
                          <div className="relative w-full rounded-xl py-4 px-4 bg-gradient-to-r from-purple-50/80 to-indigo-50/80 dark:from-purple-950/20 dark:to-indigo-950/20 border border-purple-200/70 dark:border-purple-800/70">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-purple-100/80 dark:bg-purple-900/20 flex items-center justify-center">
                                <link.icon className="w-5 h-5 text-purple-500 dark:text-purple-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-purple-600 dark:text-purple-300">{link.label}</span>
                                  {link.badge && (
                                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-purple-400 text-white rounded-full">
                                      {link.badge}
                                    </span>
                                  )}
                                  {link.locked && (
                                    <Lock className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                </div>
                                {link.description && (
                                  <p className="text-[11px] text-purple-500/70 dark:text-purple-400/70">{link.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : link.secondary && !isActive(link.href) ? (
                          /* Secondary actions - softer, less saturated */
                          <div className="relative w-full rounded-xl py-3 px-4 bg-slate-50/80 dark:bg-slate-800/50 border border-slate-200/60 dark:border-slate-700/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-700/50 flex items-center justify-center">
                                <link.icon className="w-4 h-4 text-slate-500 dark:text-slate-400" />
                              </div>
                              <div>
                                <span className="font-medium text-slate-700 dark:text-slate-300">{link.label}</span>
                                {link.description && (
                                  <p className="text-[11px] text-slate-500/80 dark:text-slate-400/70">{link.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant={isActive(link.href) ? 'default' : 'ghost'}
                            className={`w-full justify-start gap-3 rounded-xl py-6 ${
                              isActive(link.href)
                                ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                                : ''
                            }`}
                          >
                            <link.icon className="w-5 h-5" />
                            {link.label}
                          </Button>
                        )}
                      </Link>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-200 dark:bg-slate-700 my-4" />

                  {/* === CONTRIBUTE GROUP === */}
                  <div className="space-y-2">
                    {contributeLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                      >
                        {link.urgent && !isActive(link.href) ? (
                          <div className="relative w-full rounded-xl py-4 px-4 bg-gradient-to-r from-red-50 to-orange-50 dark:from-red-950/30 dark:to-orange-950/30 border border-red-200 dark:border-red-800/50 shadow-sm">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                                <link.icon className="w-5 h-5 text-red-600 dark:text-red-400" />
                              </div>
                              <div>
                                <span className="font-semibold text-red-700 dark:text-red-300">{link.label}</span>
                                {link.description && (
                                  <p className="text-[11px] text-red-600/70 dark:text-red-400/70">{link.description}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant={isActive(link.href) ? 'default' : 'ghost'}
                            className={`w-full justify-start gap-3 rounded-xl py-6 ${
                              isActive(link.href)
                                ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                                : link.highlight
                                ? 'text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-950/30 dark:text-pink-400'
                                : ''
                            }`}
                          >
                            <link.icon className={`w-5 h-5 ${link.highlight ? 'text-pink-500' : ''}`} />
                            {link.label}
                          </Button>
                        )}
                      </Link>
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-200 dark:bg-slate-700 my-4" />

                  {/* === LEARN GROUP === */}
                  <div className="space-y-2">
                    {/* Why LinkTheMissing - Prominent in mobile menu */}
                    <Link to="/why" onClick={() => setIsOpen(false)}>
                      <div className="relative w-full rounded-xl py-4 px-4 bg-gradient-to-r from-trust-blue-50 to-indigo-50 dark:from-trust-blue-950/30 dark:to-indigo-950/30 border border-trust-blue-200 dark:border-trust-blue-800">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-trust-blue-100 dark:bg-trust-blue-900/30 flex items-center justify-center">
                            <HelpCircle className="w-5 h-5 text-trust-blue-600 dark:text-trust-blue-400" />
                          </div>
                          <div>
                            <span className="font-semibold text-trust-blue-700 dark:text-trust-blue-300">Why LinkTheMissing</span>
                            <p className="text-[11px] text-trust-blue-600/70 dark:text-trust-blue-400/70">Our mission & approach</p>
                          </div>
                        </div>
                      </div>
                    </Link>
                    
                    {/* Contact Us - Easy access on mobile */}
                    <Link to="/contact" onClick={() => setIsOpen(false)}>
                      <Button
                        variant={isActive('/contact') ? 'default' : 'ghost'}
                        className={`w-full justify-start gap-3 rounded-xl py-6 ${
                          isActive('/contact')
                            ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                            : ''
                        }`}
                        data-testid="mobile-contact-link"
                      >
                        <Mail className="w-5 h-5" />
                        Contact Us
                      </Button>
                    </Link>
                    
                    {learnLinks.map((link) => (
                      link.comingSoon ? (
                        <div key={link.href} className="opacity-60">
                          <Button
                            variant="ghost"
                            disabled
                            className="w-full justify-start gap-3 rounded-xl py-6 cursor-not-allowed"
                          >
                            <link.icon className="w-5 h-5" />
                            {link.label}
                            <Badge variant="outline" className="ml-auto text-[10px] py-0 px-1.5 bg-amber-100 text-amber-700 border-amber-300">Coming Soon</Badge>
                          </Button>
                        </div>
                      ) : (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                      >
                        <Button
                          variant={isActive(link.href) ? 'default' : 'ghost'}
                          className={`w-full justify-start gap-3 rounded-xl py-6 ${
                            isActive(link.href)
                              ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                              : ''
                          }`}
                        >
                          <link.icon className="w-5 h-5" />
                          {link.label}
                        </Button>
                      </Link>
                      )
                    ))}
                  </div>

                  {/* Divider */}
                  <div className="h-px bg-slate-200 dark:bg-slate-700 my-4" />

                  {/* === ADMIN GROUP === */}
                  {isAuthenticated && user?.is_admin && (
                    <>
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-2 mb-2">
                          Admin Tools
                        </p>
                        <Link to="/admin/dashboard" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-emerald-50/80 dark:bg-emerald-950/30 border border-emerald-200/60 dark:border-emerald-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
                                <Gauge className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                              </div>
                              <span className="font-medium text-emerald-700 dark:text-emerald-300">Admin Dashboard</span>
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/investigate" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-violet-50/80 dark:bg-violet-950/30 border border-violet-200/60 dark:border-violet-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
                                <Bot className="w-4 h-4 text-violet-600 dark:text-violet-400" />
                              </div>
                              <span className="font-medium text-violet-700 dark:text-violet-300">AI Investigator</span>
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/add-case" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-blue-50/80 dark:bg-blue-950/30 border border-blue-200/60 dark:border-blue-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                                <UserPlus className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                              </div>
                              <span className="font-medium text-blue-700 dark:text-blue-300">Add Missing Person</span>
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/case-review" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-purple-50/80 dark:bg-purple-950/30 border border-purple-200/60 dark:border-purple-800/60">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                                  <FileCheck className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                </div>
                                <span className="font-medium text-purple-700 dark:text-purple-300">Case Review</span>
                              </div>
                              {pendingReviewCount > 0 && (
                                <Badge variant="destructive" className="h-6 px-2">
                                  {pendingReviewCount}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/duplicates" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-rose-50/80 dark:bg-rose-950/30 border border-rose-200/60 dark:border-rose-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                                <Scale className="w-4 h-4 text-rose-600 dark:text-rose-400" />
                              </div>
                              <span className="font-medium text-rose-700 dark:text-rose-300">Duplicate Review</span>
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/merge-cases" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-indigo-50/80 dark:bg-indigo-950/30 border border-indigo-200/60 dark:border-indigo-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                                <FolderSearch className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                              </div>
                              <span className="font-medium text-indigo-700 dark:text-indigo-300">Merge Cases</span>
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/facial-matches" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-cyan-50/80 dark:bg-cyan-950/30 border border-cyan-200/60 dark:border-cyan-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                                <Camera className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
                              </div>
                              <span className="font-medium text-cyan-700 dark:text-cyan-300">Facial Matches</span>
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/jobs" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-amber-50/80 dark:bg-amber-950/30 border border-amber-200/60 dark:border-amber-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                                <Zap className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                              </div>
                              <span className="font-medium text-amber-700 dark:text-amber-300">Background Jobs</span>
                            </div>
                          </div>
                        </Link>
                        <Link to="/admin/external" onClick={() => setIsOpen(false)}>
                          <div className="relative w-full rounded-xl py-3 px-4 bg-green-50/80 dark:bg-green-950/30 border border-green-200/60 dark:border-green-800/60">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                                <Database className="w-4 h-4 text-green-600 dark:text-green-400" />
                              </div>
                              <span className="font-medium text-green-700 dark:text-green-300">External Cases</span>
                            </div>
                          </div>
                        </Link>
                      </div>
                      {/* Divider */}
                      <div className="h-px bg-slate-200 dark:bg-slate-700 my-4" />
                    </>
                  )}

                  {/* === ACCOUNT GROUP === */}
                  <div className="space-y-2">
                    {/* Settings - Only for authenticated users */}
                    {isAuthenticated && user && (
                      <Link to="/settings" onClick={() => setIsOpen(false)}>
                        <Button
                          variant={isActive('/settings') ? 'default' : 'ghost'}
                          className={`w-full justify-start gap-3 rounded-xl py-6 ${
                            isActive('/settings')
                              ? 'bg-safety-orange-500 hover:bg-safety-orange-600 text-white'
                              : ''
                          }`}
                        >
                          <Settings className="w-5 h-5" />
                          Settings
                        </Button>
                      </Link>
                    )}
                  
                    {/* Dark Mode Toggle */}
                    <Button
                      variant="ghost"
                      onClick={toggleDarkMode}
                      className="w-full justify-start gap-3 rounded-xl py-6"
                    >
                      {darkMode ? (
                        <>
                          <Sun className="w-5 h-5 text-yellow-500" />
                          Light Mode
                        </>
                      ) : (
                        <>
                          <Moon className="w-5 h-5 text-slate-600" />
                          Dark Mode
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </motion.header>
  );
};
